/**
 * Versão do app instalada × versão exigida.
 *
 * DOIS MODOS, e a diferença é o que a pessoa pode fazer:
 *
 *   AVISO (LATEST_VERSION) ...... "saiu uma versão nova". Dispensável, aparece
 *                                 no máximo 3 vezes por par de versões.
 *   BLOQUEIO (MIN_VERSION) ...... "esta versão não roda mais". Sem saída a não
 *                                 ser atualizar. É o portão de quebra de
 *                                 contrato — quando o app antigo passa a
 *                                 conversar errado com o backend.
 *
 * O bloqueio SÓ EXISTE NO NATIVO. Na web não há loja para mandar a pessoa, e
 * o bundle servido é sempre o mais novo — bloquear ali seria trancar alguém
 * numa porta que já está aberta.
 *
 * FAIL-OPEN, e isto não é detalhe: se a leitura do CONFIG falhar, o app ABRE.
 * Um portão que tranca por erro de rede transforma uma oscilação de conexão
 * numa pane geral, e a pessoa não tem como sair — nem desinstalando, porque o
 * problema volta na reinstalação.
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { doc, getDoc } from 'firebase/firestore';

import { db } from '../firebaseConfig';

export const APP_VERSION_CONFIG_PATH = 'APP_CONFIG/APP_VERSION';

const STORE_URLS = {
  ios: 'https://apps.apple.com/us/app/lesvita-fit/id6753772111',
  android: 'https://play.google.com/store/apps/details?id=com.lesvita.app',
};

const normalizeVersion = (version) => String(version || '0')
  .trim()
  .replace(/^v/i, '')
  .split(/[+-]/)[0];

export const compareAppVersions = (installedVersion, latestVersion) => {
  const installed = normalizeVersion(installedVersion).split('.').map((part) => Number.parseInt(part, 10) || 0);
  const latest = normalizeVersion(latestVersion).split('.').map((part) => Number.parseInt(part, 10) || 0);
  const length = Math.max(installed.length, latest.length);

  for (let index = 0; index < length; index += 1) {
    const currentPart = installed[index] || 0;
    const latestPart = latest[index] || 0;
    if (currentPart < latestPart) return -1;
    if (currentPart > latestPart) return 1;
  }
  return 0;
};

const configValue = (config, ...keys) => {
  for (const key of keys) {
    if (config?.[key] !== undefined && config?.[key] !== null) return config[key];
  }
  return undefined;
};

const promptStorageKey = ({ platform, installedVersion, latestVersion }) => (
  `lesvita:update-prompt:${platform}:${normalizeVersion(installedVersion)}:${normalizeVersion(latestVersion)}`
);

const readPromptCount = (update) => {
  try {
    return Number.parseInt(localStorage.getItem(promptStorageKey(update)), 10) || 0;
  } catch {
    return 0;
  }
};

export const markUpdatePromptShown = (update) => {
  try {
    const key = promptStorageKey(update);
    localStorage.setItem(key, String(readPromptCount(update) + 1));
  } catch {
    // Armazenamento indisponível não deve impedir o app de abrir.
  }
};

export async function getAvailableAppUpdate() {
  if (!Capacitor.isNativePlatform()) return null;

  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return null;

  try {
    const [appInfo, configSnap] = await Promise.all([
      App.getInfo(),
      getDoc(doc(db, 'APP_CONFIG', 'APP_VERSION')),
    ]);
    if (!configSnap.exists()) return null;

    const config = configSnap.data() || {};

    // ── BLOQUEIO ──
    // Vem ANTES do `ACTIVE`: desligar o aviso de versão nova não pode, por
    // tabela, desligar o portão que existe porque a versão antiga quebrou.
    // São duas decisões diferentes, com dois campos diferentes.
    const minVersion = String((platform === 'ios'
      ? configValue(config, 'IOS_MIN_VERSION', 'ios_min_version', 'MIN_VERSION', 'min_version')
      : configValue(config, 'ANDROID_MIN_VERSION', 'android_min_version', 'MIN_VERSION', 'min_version'))
      || '').trim();

    if (minVersion && compareAppVersions(appInfo.version, minVersion) < 0) {
      return {
        obrigatorio: true,
        platform,
        installedVersion: appInfo.version,
        latestVersion: minVersion,
        title: configValue(config, 'BLOCK_TITLE', 'block_title')
          || 'Atualize para continuar',
        message: configValue(config, 'BLOCK_MESSAGE', 'block_message')
          || 'Esta versão do LesVita não é mais compatível. Atualize pela loja para continuar de onde parou.',
        storeUrl: platform === 'ios'
          ? configValue(config, 'IOS_STORE_URL', 'ios_store_url') || STORE_URLS.ios
          : configValue(config, 'ANDROID_STORE_URL', 'android_store_url') || STORE_URLS.android,
      };
    }

    // ── AVISO ──
    const enabled = configValue(config, 'ACTIVE', 'active', 'ENABLED', 'enabled');
    if (enabled === false) return null;

    const latestVersion = String((platform === 'ios'
      ? configValue(config, 'IOS_LATEST_VERSION', 'ios_latest_version', 'LATEST_VERSION', 'latest_version')
      : configValue(config, 'ANDROID_LATEST_VERSION', 'android_latest_version', 'LATEST_VERSION', 'latest_version'))
      || '').trim();
    if (!latestVersion || compareAppVersions(appInfo.version, latestVersion) >= 0) return null;

    const configuredLimit = Number(configValue(config, 'MAX_PROMPTS', 'max_prompts'));
    const maxPrompts = Number.isFinite(configuredLimit)
      ? Math.min(3, Math.max(1, Math.round(configuredLimit)))
      : 3;
    const update = {
      obrigatorio: false,
      platform,
      installedVersion: appInfo.version,
      latestVersion,
      maxPrompts,
      title: configValue(config, 'TITLE', 'title') || 'Encontramos uma atualização',
      message: configValue(config, 'MESSAGE', 'message')
        || 'Há uma versão mais recente do LesVita disponível. Atualize para continuar com todas as novidades.',
      storeUrl: platform === 'ios'
        ? configValue(config, 'IOS_STORE_URL', 'ios_store_url') || STORE_URLS.ios
        : configValue(config, 'ANDROID_STORE_URL', 'android_store_url') || STORE_URLS.android,
    };

    return readPromptCount(update) < maxPrompts ? update : null;
  } catch (error) {
    console.warn('[AppUpdate] Não foi possível verificar a versão:', error?.message);
    return null;
  }
}
